import { Link } from "react-router-dom";
import { CheckCircle2, ChevronRight } from "lucide-react";

/* IndianAirports.com — full About article (owner-provided copy, formatted for the web). */

const P = ({ children }) => <p className="text-sm text-zinc-600 leading-relaxed">{children}</p>;

const Section = ({ title, children }) => (
  <section className="space-y-3">
    <h2 className="font-heading font-900 text-lg md:text-lg text-zinc-900">{title}</h2>
    {children}
  </section>
);

const ItemGrid = ({ items }) => (
  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-1.5">
    {items.map((t) => (
      <div key={t} className="flex items-start gap-1.5 text-xs text-zinc-600">
        <CheckCircle2 className="w-3.5 h-3.5 text-blue-600 shrink-0 mt-[1px]" />
        <span>{t}</span>
      </div>
    ))}
  </div>
);

const PROFILE_ITEMS = [
  "Official airport name", "IATA and ICAO codes", "Airport category and operating status",
  "City, district and state or union territory", "Complete address and map location", "Geographic coordinates",
  "Elevation above sea level", "Time zone", "Terminal information",
  "Domestic and international operations", "Airlines serving the airport", "Nonstop routes and connections",
  "Arrival and departure information", "Airport telephone numbers and contacts", "Airport operator and official website",
  "Check-in and security guidance", "Baggage services", "Lost-and-found information",
  "Customs and immigration facilities", "Lounges", "Restaurants and cafés",
  "Shopping and duty-free services", "Wi-Fi and charging facilities", "Medical assistance",
  "Prayer and quiet rooms", "Baby-care facilities", "Accessibility services",
  "Parking", "Car rentals", "Taxi and public transportation",
  "Nearby hotels", "Weather and travel advisories", "Traveler reviews, questions and local tips",
];

const REGION_ITEMS = [
  "City overview", "District information", "State or union territory profile",
  "Location within India", "Regional map", "Distance from airport to city center",
  "Nearby cities and towns", "Business and commercial areas", "Popular tourism destinations",
  "Historical and cultural attractions", "Religious and pilgrimage sites", "Beaches, mountains, parks and nature",
  "Universities and educational institutions", "Hospitals and medical centers", "Convention and exhibition facilities",
  "Railway stations", "Metro and bus connections", "Major highways",
  "Nearby airports", "Typical travel times", "Local climate and seasonal conditions",
  "Languages commonly spoken", "Local cuisine and shopping areas", "Emergency and visitor-assistance information",
];

const VERTICALS = [
  "Airports", "Airlines", "Flights and routes", "Ground transportation", "Taxis",
  "Hotels and accommodations", "Restaurants and dining", "Tourism and attractions", "Business travel",
  "Medical travel", "Student travel", "Pilgrimage travel", "Accessible travel", "Family travel",
  "Senior travel", "Baggage assistance", "Travel documents", "Weather and disruptions",
  "Traveler questions and reviews",
];

const TAXI_REQUEST = [
  "Arrival airport", "Airline and flight number", "Arrival date and expected time", "Pickup terminal",
  "Passenger's name and contact details", "Number of passengers", "Number and type of bags",
  "Destination address", "One-way or round-trip transportation", "Required pickup date and time",
  "Vehicle preference (sedan, SUV, van or larger)", "Child-seat requirement", "Wheelchair-accessible vehicle",
  "Meet-and-greet request", "Additional stops", "Return-airport transfer", "Preferred language", "Special instructions",
];

const TAXI_VALUABLE = [
  "International visitors", "Travelers unfamiliar with the local language", "Senior citizens",
  "Families with children", "Groups carrying substantial luggage", "People requiring accessible transportation",
  "Late-night and early-morning arrivals", "Travelers continuing to distant cities",
  "Business travelers needing scheduled pickup", "Medical travelers going to hospitals",
  "Visitors attending weddings, conferences or major events",
];

const HUB_ITEMS = [
  "Trip name and description", "Origin and destination", "Passenger information", "Flight details",
  "Airline and airport information", "Departure and arrival dates", "Flight segments and layovers",
  "Booking reference or PNR", "Ticket information", "Boarding-pass copies",
  "Trusted-traveler information", "Airline loyalty numbers", "Baggage information",
  "Luggage-tag photographs", "Taxi pickup requests", "Hotel reservations",
  "Train, bus, boat and local transport", "Visa and passport information", "Travel insurance documents",
  "Health records selected by the traveler", "Arrival forms and documents", "Packing lists",
  "Travel notes", "Photographs and receipts", "Trip expenses", "Emergency contacts",
];

const BUSINESS_ITEMS = [
  "Taxi and transportation operators", "Hotels and guest houses", "Restaurants and cafés",
  "Tour operators", "Travel agencies", "Car-rental companies", "Parking providers",
  "Luggage and courier services", "Medical-transportation providers", "Accessibility-service providers",
  "Local attractions and visitor centers",
];

const COMMUNITY_ITEMS = [
  "Rate airport facilities", "Review transportation experiences", "Share terminal-navigation tips",
  "Report outdated information", "Ask airport-specific questions", "Recommend helpful services",
  "Describe accessibility experiences", "Share baggage and connection advice",
  "Upload appropriate airport photographs", "Save favorite airports", "Follow airport updates",
];

const JOURNEY_STEPS = [
  "Discover the airport and destination on IndianAirports.com.",
  "Review terminals, airlines, routes and facilities.",
  "Learn about the city, district and state.",
  "Explore transportation, hotels and important destinations.",
  "Submit an airport taxi request.",
  "Save selected information to My Travel Hub.",
  "Add tickets, passengers, documents and trip notes.",
  "Access the organized trip information throughout the journey.",
];

const TRAVELER_TYPES = [
  ["Domestic travelers", "Compare routes, review airport facilities, understand terminal arrangements and plan transportation from the airport to the final destination."],
  ["International travelers", "Find information about immigration, customs, currency services, local transportation, connecting flights, nearby hotels and regional destinations."],
  ["Business travelers", "Identify commercial districts, convention centers, airport hotels, lounges, transportation services and efficient connections to nearby cities."],
  ["Families and senior travelers", "Review accessibility, medical assistance, baggage services, family facilities, rest areas and transportation choices before beginning the journey."],
  ["Students", "Discover the most convenient airport, nearby railway or bus connections, local transportation and estimated travel distances to colleges and universities."],
  ["Medical travelers", "Identify airports near major hospitals, request suitable transportation and store relevant journey information in My Travel Hub."],
  ["Pilgrimage and tourism travelers", "Discover airports serving pilgrimage centers, heritage sites, beaches, wildlife destinations and mountain regions — even in another district or state."],
];

export default function IAAboutArticle() {
  return (
    <article className="space-y-8" data-testid="india-about-article">
      <header className="space-y-3">
        <h2 className="font-heading font-900 text-2xl md:text-3xl text-zinc-900">
          Every Indian Airport, City and Journey in One Place
        </h2>
        <P>
          India's airports are more than arrival and departure points. They are gateways to cities, business centers,
          tourism destinations, pilgrimage routes, educational institutions, medical facilities and communities across the
          country. Yet travelers often must visit several websites to find airport details, flight information,
          transportation choices, terminal guidance, hotels and reliable information about their destination.
        </P>
        <P>
          IndianAirports.com is being developed to bring this information together in one convenient, traveler-friendly platform.
        </P>
        <P>
          From India's largest international gateways to regional airports serving smaller cities and emerging destinations,
          IndianAirports.com will help travelers explore airports, understand the cities and states they serve, arrange local
          transportation and organize their entire journey through an integrated connection with{" "}
          <Link to="/travel-hub" className="text-blue-700 font-700 hover:underline">My Travel Hub</Link> from TravelerPulse.pro.
        </P>
        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 text-center">
          <p className="text-xs font-800 uppercase tracking-wide text-blue-600">Our goal is simple</p>
          <p className="mt-1 font-heading font-900 text-xl text-blue-900">Every Indian Airport. One Trusted Guide.</p>
        </div>
        <P>
          Whether someone is traveling for business, a family vacation, education, medical care, a wedding, pilgrimage or a
          journey home, IndianAirports.com will provide practical information for planning before departure, navigating the
          airport and continuing safely to the final destination.
        </P>
      </header>

      <Section title="Detailed Information for Every Indian Airport">
        <P>
          Each airport will have a dedicated information page designed to answer the questions travelers commonly ask before
          and during a trip. Airport profiles may include:
        </P>
        <ItemGrid items={PROFILE_ITEMS} />
        <P>
          Information will be organized into clear sections so travelers can quickly find what they need without searching
          through a long, complicated page.
        </P>
      </Section>

      <Section title="Discover the City, District and State Beyond the Airport">
        <P>
          An airport guide should not end at the terminal doors. Every airport is connected to a broader geographic area, and
          many airports serve several nearby cities, districts and tourism destinations. IndianAirports.com will provide
          detailed geographic information about the airport's surrounding region, including:
        </P>
        <ItemGrid items={REGION_ITEMS} />
        <P>
          This city-and-state information will help travelers understand not only where an airport is located, but also where
          they can go from it. A traveler arriving at an airport may actually be continuing to another city, a hill station,
          an industrial area, a pilgrimage center or a neighboring district — IndianAirports.com will make these onward
          connections easier to discover and plan.
        </P>
      </Section>

      <Section title="Geographic and Vertical Information in One Connected Platform">
        <P>Travelers will be able to explore information through a geographic structure:</P>
        <div className="flex flex-wrap items-center gap-1 text-xs font-800 text-zinc-700" data-testid="india-about-geo-chain">
          {["India", "State or UT", "District", "City", "Airport", "Destination"].map((s, i, arr) => (
            <span key={s} className="inline-flex items-center gap-1">
              <span className="bg-white border border-zinc-300 rounded-lg px-2.5 py-1">{s}</span>
              {i < arr.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-orange-500" />}
            </span>
          ))}
        </div>
        <P>They will also be able to explore through service-focused verticals:</P>
        <div className="flex flex-wrap gap-1.5">
          {VERTICALS.map((v) => (
            <span key={v} className="text-[11px] font-700 bg-zinc-100 text-zinc-700 border border-zinc-200 rounded-full px-2.5 py-1">{v}</span>
          ))}
        </div>
        <P>
          Connecting geographic and vertical information will create a more useful experience than a conventional airport
          directory. A traveler viewing an airport will be able to move naturally from terminal information to taxi options,
          nearby hotels, city guides, important destinations and personal trip planning.
        </P>
      </Section>

      <Section title="Airport-to-Destination Taxi Planning">
        <P>
          Finding dependable transportation after landing can be one of the most stressful parts of a journey — especially in
          an unfamiliar city, late at night, or with children, elderly passengers or substantial luggage. IndianAirports.com
          will include a dedicated <b className="text-zinc-800">Airport Taxi Request and Lead Creation Service</b> to help
          travelers plan transportation in advance. A traveler will be able to submit details such as:
        </P>
        <ItemGrid items={TAXI_REQUEST} />
        <P>
          Where available, the request may be shared with appropriate participating taxi operators, transportation companies
          or travel-service providers serving that airport and destination. Travelers may then receive availability
          information, estimated pricing or a service proposal directly from participating providers.
        </P>
        <p className="text-xs text-zinc-500 bg-amber-50 border border-amber-200 rounded-xl p-3">
          Submitting a taxi request creates a transportation inquiry and does not constitute a confirmed booking until a
          provider accepts and confirms it.
        </p>
        <P>The service can be especially valuable for:</P>
        <ItemGrid items={TAXI_VALUABLE} />
        <P>Taxi providers can also receive organized, qualified travel inquiries containing the information needed to respond efficiently.</P>
      </Section>

      <Section title="Integration with My Travel Hub from TravelerPulse.pro">
        <P>
          After finding an airport, route, taxi option, hotel or destination on IndianAirports.com, a traveler will be able to
          save relevant information to a personal trip. My Travel Hub can keep important journey details together, including:
        </P>
        <ItemGrid items={HUB_ITEMS} />
        <P>
          Travelers control which information they store and share. Sensitive documents and personal data are protected with
          strong security, privacy settings and clearly explained consent controls.
        </P>
      </Section>

      <Section title="One Journey Across Two Connected Platforms">
        <ol className="space-y-1.5">
          {JOURNEY_STEPS.map((s, i) => (
            <li key={s} className="flex items-start gap-2.5 text-sm text-zinc-600">
              <span className="shrink-0 w-5 h-5 rounded-full bg-blue-700 text-white text-[10px] font-800 inline-flex items-center justify-center mt-0.5">{i + 1}</span>
              {s}
            </li>
          ))}
        </ol>
        <P>
          Instead of repeatedly searching for the same details, travelers can create a personal travel record that remains
          available before departure, during the trip and after returning home.
        </P>
      </Section>

      <Section title="Information for Different Types of Travelers">
        <div className="grid sm:grid-cols-2 gap-3">
          {TRAVELER_TYPES.map(([t, d]) => (
            <div key={t} className="bg-white border border-zinc-200 rounded-2xl p-4">
              <div className="text-sm font-800 text-zinc-800">{t}</div>
              <p className="mt-1 text-xs text-zinc-500 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section title="A Platform for Airports, Airlines and Local Businesses">
        <P>IndianAirports.com can also connect travelers with legitimate businesses serving airport communities, including:</P>
        <ItemGrid items={BUSINESS_ITEMS} />
        <P>
          Participating businesses can maintain informative profiles, respond to traveler inquiries and promote relevant
          services without overwhelming users with unrelated advertising. Sponsored or promoted content is clearly identified,
          and provider participation is not presented as an endorsement unless a formal verification process has been completed.
        </P>
      </Section>

      <Section title="Traveler Knowledge and Community Participation">
        <P>Official information is essential, but travelers also benefit from recent practical experiences shared by other passengers. Users can:</P>
        <ItemGrid items={COMMUNITY_ITEMS} />
        <P>
          Reviews are moderated for authenticity, privacy, relevance and respectful communication. Airport operators, airlines
          and participating businesses may respond to questions or address reported concerns.
        </P>
      </Section>

      <Section title="Reliable, Transparent and Frequently Updated">
        <P>
          Airport operations, airline routes, terminal arrangements, transportation services and entry requirements can
          change. IndianAirports.com identifies the source and update date of important information whenever practical.
          Travelers should confirm time-sensitive details with the relevant airport, airline, government authority or service
          provider before traveling.
        </P>
        <p className="text-xs text-zinc-500 bg-zinc-50 border border-zinc-200 rounded-xl p-3">
          IndianAirports.com is an independent travel-information platform unless expressly identified otherwise. It does not
          imply affiliation with the Airports Authority of India, any airport operator, airline or government department
          without authorization.
        </p>
      </Section>

      <Section title="Our Vision">
        <P>
          Our vision is to build the most comprehensive and traveler-friendly digital gateway to India's airports, cities,
          states and onward destinations. IndianAirports.com will not be merely a list of airports — it will be a connected
          travel ecosystem where visitors can discover an airport, understand its surrounding region, plan local
          transportation, organize a taxi inquiry and transfer important information into My Travel Hub.
        </P>
        <P>
          From the first search to the final destination, IndianAirports.com and TravelerPulse.pro will work together to make
          travel more organized, informed and manageable.
        </P>
        <div className="bg-gradient-to-br from-blue-700 to-blue-900 text-white rounded-2xl p-6 text-center" data-testid="india-about-closing">
          <div className="font-heading font-900 text-xl"><span className="text-blue-200">Indian</span><span className="text-orange-300">Airports</span>.com</div>
          <p className="mt-1.5 text-sm font-700">Every Indian Airport. Every City Connection. One Trusted Travel Guide.</p>
          <p className="mt-1 text-xs text-blue-200">Explore the airport. Discover the destination. Arrange your journey. Save everything in My Travel Hub.</p>
        </div>
      </Section>
    </article>
  );
}
